<template>
  <div class="invite-form">
    <div class="invite-form__header">
      <span class="invite-form__icon">🎉</span>
      <div>
        <h3 class="invite-form__title">Convite Digital</h3>
        <p class="invite-form__subtitle">Personalize o convite que seus convidados verão antes de acessar a lista.</p>
      </div>
    </div>

    <!-- Localização -->
    <div class="invite-form__field mb-3">
      <label class="invite-form__label">
        <i class="fas fa-map-marker-alt me-1" style="color:#ff50a3"></i>
        Local do evento
      </label>
      <input
        type="text"
        class="form-control"
        v-model="localForm.location"
        placeholder="Ex: Salão de Festas Villa Bella, Rua das Flores, 123 – São Paulo"
        maxlength="200"
        @input="emitUpdate"
      />
      <small class="text-muted">Endereço completo ou nome do local</small>
    </div>

    <!-- Data e Hora -->
    <div class="invite-form__field mb-3">
      <label class="invite-form__label">
        <i class="fas fa-calendar-alt me-1" style="color:#ff50a3"></i>
        Data e horário do evento
      </label>
      <div class="invite-datetime-row">
        <input
          type="date"
          class="form-control"
          :class="{ 'input-empty': !localForm.eventDate }"
          v-model="localForm.eventDate"
          @input="emitUpdate"
        />
        <input
          type="time"
          class="form-control invite-time-input"
          :class="{ 'input-empty': !localForm.eventTime }"
          v-model="localForm.eventTime"
          @input="emitUpdate"
        />
      </div>
      <small class="text-muted">Opcional</small>
    </div>

    <!-- Vestimenta -->
    <div class="invite-form__field mb-3">
      <label class="invite-form__label">
        <i class="fas fa-tshirt me-1" style="color:#ff50a3"></i>
        Vestimenta / Dress Code
      </label>
      <input
        type="text"
        class="form-control"
        v-model="localForm.dressCode"
        placeholder="Ex: Social, Casual Chic, Traje a Rigor..."
        maxlength="100"
        @input="emitUpdate"
      />
      <small class="text-muted">Opcional</small>
    </div>

    <!-- Fotos do convite -->
    <div class="invite-form__field mb-3">
      <label class="invite-form__label">
        <i class="fas fa-images me-1" style="color:#ff50a3"></i>
        Fotos do convite
        <span class="badge bg-light text-muted ms-1">{{ filledCount }}/{{ maxPhotos }}</span>
        <small v-if="filledCount > 1" class="invite-photos-hint-drag ms-2">
          <i class="fas fa-arrows-alt me-1"></i>Arraste para reordenar
        </small>
      </label>

      <!-- Grade de slots fixos -->
      <div ref="photosGrid" class="invite-photos-grid">
        <div
          v-for="(slot, index) in slots"
          :key="'slot-' + index"
          :data-slot-index="index"
          :class="[
            'invite-photo-slot',
            slot ? 'invite-photo-slot--filled' : 'invite-photo-slot--empty',
            dragOverIndex === index ? 'invite-photo-slot--dragover' : '',
            dragSourceIndex === index ? 'invite-photo-slot--dragging' : '',
          ]"
          @pointerdown="slot && onPointerDown($event, index)"
          @click="!slot && triggerPhotoInput(index)"
        >
          <!-- Slot preenchido -->
          <template v-if="slot">
            <img :src="slot.dataUrl" alt="Foto do convite" draggable="false" />
            <button
              type="button"
              class="invite-photo-thumb__remove"
              @pointerdown.stop
              @click.stop="removePhoto(index)"
              title="Remover foto"
            >
              <i class="fas fa-times"></i>
            </button>
          </template>
          <!-- Slot vazio -->
          <template v-else>
            <i class="fas fa-plus"></i>
            <span>Adicionar</span>
          </template>
        </div>
      </div>

      <input
        ref="photoInput"
        type="file"
        accept="image/*"
        class="d-none"
        @change="handlePhotoSelected"
      />

      <div class="invite-photos-hints">
        <small class="text-muted">Máximo {{ maxPhotos }} fotos. Formatos: JPG, PNG, WEBP.</small>
      </div>
    </div>

    <!-- Preview do convite -->
    <div v-if="hasAnyContent" class="invite-preview mt-4">
      <p class="invite-preview__label">
        <i class="fas fa-eye me-1"></i>
        Pré-visualização do convite
      </p>
      <ConviteCard
        :eventName="eventName"
        :photos="filledSlots.map(s => s.dataUrl)"
        :eventDate="localForm.eventDate"
        :eventTime="localForm.eventTime"
        :location="localForm.location"
        :dressCode="localForm.dressCode"
      />
    </div>

    <!-- Cropper de fotos do convite -->
    <ImageCropperModal
      :visible="cropperModal.visible"
      :src="cropperModal.src"
      :aspect-ratio="4/3"
      title="Ajuste a foto do convite"
      subtitle="Posicione e enquadre a imagem como preferir."
      @confirm="onCropConfirm"
      @cancel="onCropCancel"
    />

  </div>
</template>

<script>
import ImageCropperModal from '@/components/ImageCropperModal.vue';
import ConviteCard from '@/components/ConviteCard.vue';

export default {
  name: 'InviteForm',

  components: { ImageCropperModal, ConviteCard },

  props: {
    // Nome do evento (para exibir no preview)
    eventName: {
      type: String,
      default: '',
    },
    // Máximo de fotos permitidas
    maxPhotos: {
      type: Number,
      default: 5,
    },
    // Dados iniciais (para edição)
    initialData: {
      type: Object,
      default: () => ({
        location: '',
        dressCode: '',
        photos: [],
      }),
    },
  },

  emits: ['update:inviteData'],

  data() {
    return {
      localForm: {
        location: '',
        dressCode: '',
        eventDate: '',
        eventTime: '',
      },
      // Slots fixos: null = vazio, { file, dataUrl } = preenchido
      slots: Array.from({ length: this.maxPhotos }, () => null),
      dragSourceIndex: null,
      dragOverIndex: null,
      _dragGhost: null,
      pendingSlotIndex: null, // qual slot o usuário clicou
      cropperModal: {
        visible: false,
        src: '',
      },
    };
  },

  computed: {
    filledSlots() {
      return this.slots.filter(Boolean);
    },
    filledCount() {
      return this.filledSlots.length;
    },
    hasAnyContent() {
      return (
        this.localForm.location.trim() !== '' ||
        this.localForm.dressCode.trim() !== '' ||
        this.localForm.eventDate !== '' ||
        this.filledCount > 0
      );
    },
    formattedDateTime() {
      if (!this.localForm.eventDate) return '';
      const [year, month, day] = this.localForm.eventDate.split('-');
      const months = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
      let str = `${parseInt(day)} de ${months[parseInt(month) - 1]} de ${year}`;
      if (this.localForm.eventTime) {
        const [h, m] = this.localForm.eventTime.split(':');
        str += ` · ${h}h${m !== '00' ? m : ''}`;
      }
      return str;
    },
  },

  watch: {},

  created() {
    const val = this.initialData;
    if (!val) return;
    this.localForm.location = val.location || '';
    this.localForm.dressCode = val.dressCode || '';
    this.localForm.eventDate = val.eventDate || '';
    this.localForm.eventTime = val.eventTime || '';
    if (Array.isArray(val.photos) && val.photos.length > 0) {
      const newSlots = [...this.slots];
      val.photos.forEach((p, i) => {
        if (i >= this.maxPhotos) return;
        if (!p) return;
        if (typeof p === 'string') {
          // URL vinda do backend
          newSlots[i] = { file: null, dataUrl: p, url: p };
        } else if (p && typeof p === 'object' && p.url) {
          // Objeto {url, dataUrl, file} vindo do store
          newSlots[i] = { file: p.file || null, dataUrl: p.dataUrl || p.url, url: p.url || null };
        } else if (p instanceof File) {
          newSlots[i] = { file: p, dataUrl: URL.createObjectURL(p), url: null };
        }
      });
      this.slots = newSlots;
    }
  },

  beforeUnmount() {
    // Nada a liberar: usamos dataUrls do cropper (não blob URLs manuais)
  },

  methods: {
    onPointerDown(e, index) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.dragSourceIndex = index;

      // Ghost visual que segue o ponteiro
      const slot = this.slots[index];
      const ghost = document.createElement('img');
      ghost.src = slot.dataUrl;
      ghost.style.cssText = [
        'position:fixed', 'width:80px', 'height:80px', 'object-fit:cover',
        'border-radius:8px', 'pointer-events:none', 'z-index:9999',
        'opacity:0.9', 'border:2px solid #ff50a3', 'transform:scale(1.1)',
        'box-shadow:0 4px 16px rgba(0,0,0,0.25)', 'transition:none',
      ].join(';');
      document.body.appendChild(ghost);
      this._dragGhost = ghost;
      this._moveGhost(e);

      const onMove = (ev) => {
        this._moveGhost(ev);
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        const slotEl = el && el.closest('[data-slot-index]');
        const over = slotEl ? parseInt(slotEl.getAttribute('data-slot-index')) : null;
        this.dragOverIndex = (over !== null && over !== this.dragSourceIndex) ? over : null;
      };

      const onUp = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        if (this._dragGhost) { this._dragGhost.remove(); this._dragGhost = null; }
        if (this.dragOverIndex !== null) this._swapSlots(this.dragSourceIndex, this.dragOverIndex);
        this.dragSourceIndex = null;
        this.dragOverIndex = null;
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      // Liberar implicit pointer capture do mobile para que elementFromPoint funcione
      e.currentTarget.releasePointerCapture(e.pointerId);
      e.preventDefault();
    },

    _moveGhost(e) {
      if (!this._dragGhost) return;
      this._dragGhost.style.left = (e.clientX - 40) + 'px';
      this._dragGhost.style.top  = (e.clientY - 40) + 'px';
    },

    _swapSlots(src, target) {
      const newSlots = [...this.slots];
      [newSlots[src], newSlots[target]] = [newSlots[target], newSlots[src]];
      this.slots = newSlots;
      this.emitUpdate();
    },

    onDrop(targetIndex) {
      this._swapSlots(this.dragSourceIndex, targetIndex);
    },

    triggerPhotoInput(slotIndex) {
      this.pendingSlotIndex = slotIndex;
      this.$refs.photoInput.value = '';
      this.$refs.photoInput.click();
    },

    handlePhotoSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.cropperModal.src = e.target.result;
        this.cropperModal.visible = true;
      };
      reader.readAsDataURL(file);
    },

    onCropConfirm({ blob, dataUrl }) {
      this.cropperModal.visible = false;
      this.cropperModal.src = '';
      const croppedFile = new File([blob], 'foto-convite.jpg', { type: blob.type || 'image/jpeg' });
      // Vue 3 requer $set ou reatribuição para reatividade em arrays por índice
      const newSlots = [...this.slots];
      newSlots[this.pendingSlotIndex] = { file: croppedFile, dataUrl, url: null };
      this.slots = newSlots;
      this.pendingSlotIndex = null;
      this.emitUpdate();
    },

    onCropCancel() {
      this.cropperModal.visible = false;
      this.cropperModal.src = '';
      this.pendingSlotIndex = null;
    },

    removePhoto(index) {
      const newSlots = [...this.slots];
      newSlots[index] = null;
      this.slots = newSlots;
      this.emitUpdate();
    },

    emitUpdate() {
      this.$emit('update:inviteData', {
        location: this.localForm.location,
        dressCode: this.localForm.dressCode,
        eventDate: this.localForm.eventDate,
        eventTime: this.localForm.eventTime,
        photos: this.slots.filter(Boolean).map(s => ({ file: s.file || null, dataUrl: s.dataUrl, url: s.url || null })),
      });
    },

    // Método público para limpar o formulário (usado na criação ao pular)
    clear() {
      this.localForm = { location: '', dressCode: '' };
      this.slots = Array.from({ length: this.maxPhotos }, () => null);
    },
  },
};
</script>

<style scoped>
.invite-form__header {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  padding: 0.9rem 1.1rem;
  margin-bottom: 1.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.invite-form__icon {
  font-size: 1.75rem;
  line-height: 1;
  flex-shrink: 0;
}

.invite-form__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #ff50a3;
  margin: 0 0 0.15rem;
}

.invite-form__subtitle {
  font-size: 0.8rem;
  color: #6c757d;
  margin: 0;
  max-width: 400px;
  margin: 0 auto;
}

.invite-form__label {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.4rem;
  color: #333;
}

/* Grid de fotos */
.invite-datetime-row {
  display: flex;
  gap: 0.5rem;
}
.invite-time-input {
  width: 120px;
  flex-shrink: 0;
}

/* Placeholder nativo de date/time com a cor correta */
input[type="date"].input-empty::-webkit-datetime-edit,
input[type="time"].input-empty::-webkit-datetime-edit {
  color: #adb5bd;
}
input[type="date"]:not(.input-empty)::-webkit-datetime-edit,
input[type="time"]:not(.input-empty)::-webkit-datetime-edit {
  color: #212529;
}

.invite-photos-hints {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-top: 0.4rem;
}
.invite-photos-hint-drag {
  color: #ff50a3;
  font-weight: 500;
}

.invite-preview__detail--date {
  font-weight: 600;
  color: #ff50a3;
}

.invite-photos-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

/* Slot único — preenchido ou vazio */
.invite-photo-slot {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.15s, opacity 0.15s;
}

/* Slot preenchido */
.invite-photo-slot--filled {
  border: 2px solid #e0e0e0;
  cursor: grab;
  touch-action: none;
}
.invite-photo-slot--filled:active { cursor: grabbing; }

/* Feedback: sendo arrastado */
.invite-photo-slot--dragging {
  opacity: 0.4;
  transform: scale(0.95);
}

/* Feedback: alvo do drop */
.invite-photo-slot--dragover {
  outline: 2px dashed #ff50a3;
  outline-offset: 2px;
  transform: scale(1.05);
}

.invite-photo-slot--filled img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Slot vazio — age como botão de adicionar */
.invite-photo-slot--empty {
  border: 2px dashed #ccc;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  font-size: 0.7rem;
  color: #999;
  transition: border-color 0.2s, color 0.2s;
}

.invite-photo-slot--empty:hover {
  border-color: #ff50a3;
  color: #ff50a3;
}

.invite-photo-slot--empty i {
  font-size: 1.2rem;
}

.invite-photo-thumb__remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0,0,0,0.6);
  border: none;
  color: #fff;
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

/* Preview */
.invite-preview__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.invite-preview__card {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

/* Placeholder sem fotos */
.invite-preview__photo-placeholder {
  width: 100%;
  height: 160px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #ccc;
}

/* ── Mosaico ─────────────────────────────────── */
.invite-mosaic {
  width: 100%;
  display: grid;
  gap: 3px;
  background: #e0e0e0;
}

/* 1 foto: full width, proporção 16:9 */
.invite-mosaic--1 {
  grid-template-columns: 1fr;
}
.invite-mosaic--1 .mosaic-cell {
  aspect-ratio: 16 / 9;
}

/* 2 fotos: lado a lado, proporção 4:3 cada */
.invite-mosaic--2 {
  grid-template-columns: 1fr 1fr;
}
.invite-mosaic--2 .mosaic-cell {
  aspect-ratio: 4 / 3;
}

/* 3 fotos: 1 grande 16:9 em cima + 2 quadradas embaixo */
.invite-mosaic--3 {
  grid-template-columns: 1fr 1fr;
}
.invite-mosaic--3 .mosaic-cell--0 {
  grid-column: 1 / 3;
  aspect-ratio: 16 / 9;
}
.invite-mosaic--3 .mosaic-cell--1,
.invite-mosaic--3 .mosaic-cell--2 {
  aspect-ratio: 4 / 3;
}

/* 4 fotos: grade 2x2, proporção 4:3 cada */
.invite-mosaic--4 {
  grid-template-columns: 1fr 1fr;
}
.invite-mosaic--4 .mosaic-cell {
  aspect-ratio: 4 / 3;
}

/* 5 fotos: 1 grande à esquerda (portrait) + 4 pequenas à direita */
.invite-mosaic--5 {
  grid-template-columns: 1fr 1fr;
}
.invite-mosaic--5 .mosaic-cell--0 {
  grid-row: 1 / 3;
}
.invite-mosaic--5 .mosaic-cell--1,
.invite-mosaic--5 .mosaic-cell--2,
.invite-mosaic--5 .mosaic-cell--3,
.invite-mosaic--5 .mosaic-cell--4 {
  aspect-ratio: 4 / 3;
}

.mosaic-cell {
  overflow: hidden;
  background: #f0f0f0;
}
.mosaic-cell img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.2s;
}
.mosaic-cell img:hover { transform: scale(1.03); }

/* Info */

.invite-preview__info {
  padding: 1rem;
}

.invite-preview__event-name {
  font-weight: 700;
  font-size: 1rem;
  color: #222;
  margin-bottom: 0.4rem;
}

.invite-preview__detail {
  font-size: 0.85rem;
  color: #555;
  margin-bottom: 0.2rem;
}

.invite-preview__detail i {
  color: #ff50a3;
  width: 16px;
}

</style>
